import { BaseButton } from '@/app/components/buttons/BaseButton';
import type { ImportPrimaryClientsData } from '@/types/clientDetailsApiTypes';
import { InvalidCards } from '../InvalidCards';
import { InvalidTable } from '../InvalidTable';
import CheckIcon from '/public/icons/check.svg';
import CrossIcon from '/public/icons/cross.svg';

interface ResultsStepProps {
  results: ImportPrimaryClientsData;
  onClose: () => void;
}

export function ResultsStep({ results, onClose }: ResultsStepProps) {
  const hasInvalid = results.invalid_count > 0;

  return (
    <div className="w-[calc(100vw-2rem)] max-w-150 px-4 py-8 flex flex-col gap-5 max-h-[calc(100dvh-2rem)] md:w-150 md:px-9 md:py-11.5 md:max-h-[80vh] max-md:gap-4 max-md:rounded-[14px] max-md:px-0 max-md:py-6">
      <div className="flex flex-col gap-3 text-center max-md:gap-1.5 max-md:px-4">
        <h2 className="light text-blue-900 max-md:text-xl max-md:leading-[130%] max-md:tracking-[0.6px]">
          Import results
        </h2>

        <div className="flex items-center justify-center gap-5 text-left">
          <div className="flex items-center gap-3 pr-5 border-r border-grey-300">
            <div className="w-9 h-9 flex items-center justify-center rounded-full bg-status-success/10 shrink-0">
              <CheckIcon className="w-3 h-3 text-status-success max-md:h-4 max-md:w-4" />
            </div>
            <div className="flex flex-col">
              <span className="font-inter text-lg font-semibold text-status-success leading-6.75 max-md:text-base max-md:leading-normal">
                {results.valid_count}
              </span>
              <span className="font-inter text-[12px] font-normal text-status-success leading-[18px] max-md:whitespace-nowrap">
                imported successfully
              </span>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <div className="w-9 h-9 flex items-center justify-center rounded-full bg-red-500/10 shrink-0">
              <CrossIcon className="w-2.5 h-2.5 text-red-500 max-md:h-4 max-md:w-4" />
            </div>
            <div className="flex flex-col">
              <span className="font-inter text-lg font-semibold text-red-500 leading-6.75 max-md:text-base max-md:leading-normal">
                {results.invalid_count}
              </span>
              <span className="font-inter text-[12px] font-normal text-red-500 leading-[18px] max-md:whitespace-nowrap">
                failed
              </span>
            </div>
          </div>
        </div>

        {hasInvalid && (
          <>
            <div className="h-px bg-grey-300 max-md:hidden" />
            <p className="text-body-small text-grey-700 max-md:leading-normal">
              Review the failed rows below, fix the issues in your file, and try again.
            </p>
          </>
        )}
      </div>

      {hasInvalid && (
        <>
          <div className="flex-1 overflow-y-auto min-h-0 rounded-xl border border-grey-300 overflow-x-auto max-md:hidden">
            <InvalidTable rows={results.invalid} />
          </div>

          <div className="hidden min-h-0 flex-1 flex-col overflow-y-auto border-t border-grey-200 px-4 pt-4 max-md:flex">
            <InvalidCards rows={results.invalid} />
          </div>
        </>
      )}

      <div className="max-md:px-4 max-md:pt-1">
        <BaseButton intent="primary" size="full" onClick={onClose} className="max-md:text-sm max-md:leading-[140%]">
          Done
        </BaseButton>
      </div>
    </div>
  );
}
